import React from 'react';
import { motion } from 'framer-motion';
import { Target, Heart, Zap, Globe } from 'lucide-react';
import './About.css';

const About = () => {
  const features = [
    {
      icon: <Target size={32} />,
      title: "Personalized Fitness",
      description: "AI-powered workout plans tailored to individual goals, fitness levels, and preferences."
    },
    {
      icon: <Heart size={32} />,
      title: "Community Support",
      description: "Connect with like-minded fitness enthusiasts, share progress, and motivate each other."
    },
    {
      icon: <Zap size={32} />,
      title: "Real-time Tracking",
      description: "Monitor your progress with advanced analytics and real-time performance insights."
    },
    {
      icon: <Globe size={32} />,
      title: "Global Network",
      description: "Access to certified coaches and trainers from around the world."
    }
  ];

  return (
    <section className="about" id="about">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">The AllFitHive Vision</h2>
          <p className="section-subtitle">
            We're building more than just a fitness app – we're creating a movement
          </p>
        </motion.div>

        <div className="about-content">
          <motion.div
            className="about-text"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3>Why AllFitHive?</h3>
            <p>
              The fitness industry is at a crossroads. Traditional gym memberships are declining, 
              while people are increasingly seeking personalized, community-driven fitness experiences 
              that fit their busy lifestyles.
            </p>
            <p>
              AllFitHive bridges this gap by combining cutting-edge technology with the power of 
              community. We're not just another fitness app – we're building an ecosystem where 
              users, coaches, and fitness enthusiasts can thrive together.
            </p>
            <div className="mission-vision">
              <div className="mission">
                <h4>Our Mission</h4>
                <p>To democratize fitness by making personalized, community-driven health accessible to everyone.</p>
              </div>
              <div className="vision">
                <h4>Our Vision</h4>
                <p>To become the world's leading fitness community platform, transforming how people approach health and wellness.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="about-features"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            viewport={{ once: true }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        <motion.div
          className="about-stats"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="stat-item">
            <motion.div
              className="stat-number"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.8, type: "spring" }}
              viewport={{ once: true }}
            >
              500M+
            </motion.div>
            <span>Global Fitness Market</span>
          </div>
          <div className="stat-item">
            <motion.div
              className="stat-number"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.8, type: "spring", delay: 0.2 }}
              viewport={{ once: true }}
            >
              25%
            </motion.div>
            <span>Annual Growth Rate</span>
          </div>
          <div className="stat-item">
            <motion.div
              className="stat-number"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.8, type: "spring", delay: 0.4 }}
              viewport={{ once: true }}
            >
              2.5B
            </motion.div>
            <span>Active Fitness Users</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
